import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtEuro0, fmtDate, MONTHS_EL_SHORT } from "@/lib/format";
import { Gauge } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Delta } from "@/components/dashboard/Kpi";

const localToday = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`; };
const YEARS = Array.from({ length: 8 }, (_, i) => 2021 + i);
const sum = (arr, k, f) => arr.reduce((s, m) => s + m[k][f], 0);

export default function Pace() {
  const now = new Date().getFullYear();
  const [year, setYear] = useState(now);
  const [cmp, setCmp] = useState(now - 1);
  const [asOf, setAsOf] = useState(localToday());
  const { data, isLoading } = useQuery({
    queryKey: ["pms-pace", year, cmp, asOf],
    queryFn: async () => (await api.get(`/pms/pace?year=${year}&compare=${cmp}&as_of=${asOf}`)).data,
  });
  const months = data?.months || [];
  const chart = months.map((m) => ({ name: MONTHS_EL_SHORT[m.m], cur: Math.round(m.cur.rev), stly: Math.round(m.stly.rev), final: Math.round(m.final.rev) }));
  const T = { cur: sum(months, "cur", "rev"), stly: sum(months, "stly", "rev"), final: sum(months, "final", "rev") };
  const N = { cur: sum(months, "cur", "nights"), stly: sum(months, "stly", "nights"), final: sum(months, "final", "nights") };
  const sel = "rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm";
  const kpi = (l, v, ly, t) => (
    <div className="rounded-2xl bg-white border border-[#E3DED4] p-4" data-testid={t}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#6F6A60]">{l}</div>
      <div className="font-serif text-2xl text-[#45423C] tabular mt-1">{v}</div>
      {ly}
    </div>
  );
  return (
    <div className="space-y-6" data-testid="pace-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Κρατήσεις</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#45423C] mt-1 flex items-center gap-3"><Gauge className="h-8 w-8" /> Pace κρατήσεων</h1>
          <p className="text-sm text-[#6F6A60] mt-2 max-w-2xl">Έσοδα δωματίων μόνο (χωρίς F&B, extras, φόρους) ανά μήνα διαμονής, όπως ήταν κλεισμένα στην ημερομηνία αναφοράς, έναντι της ίδιας ημερομηνίας του έτους σύγκρισης.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-[#6F6A60]">Έτος<select data-testid="pace-year" className={`${sel} block mt-1`} value={year} onChange={(e) => setYear(Number(e.target.value))}>{YEARS.map((y) => <option key={y}>{y}</option>)}</select></label>
          <label className="text-xs text-[#6F6A60]">Σύγκριση με<select data-testid="pace-compare" className={`${sel} block mt-1`} value={cmp} onChange={(e) => setCmp(Number(e.target.value))}>{YEARS.map((y) => <option key={y}>{y}</option>)}</select></label>
          <label className="text-xs text-[#6F6A60]">Κρατήσεις έως<input data-testid="pace-asof" type="date" className={`${sel} block mt-1`} value={asOf} onChange={(e) => e.target.value && setAsOf(e.target.value)} /></label>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpi(`Έσοδα δωματίων ${year} (on the books)`, fmtEuro0(T.cur), <Delta cur={T.cur} ly={T.stly} testid="pace-kpi-rev-delta" />, "pace-kpi-rev")}
        {kpi(`Ίδια ημερομηνία ${cmp}`, fmtEuro0(T.stly), <span className="text-[11px] text-[#6F6A60]">έως {fmtDate(data?.as_of_compare)}</span>, "pace-kpi-stly")}
        {kpi(`Τελικό ${cmp}`, fmtEuro0(T.final), <span className="text-[11px] text-[#6F6A60]">{T.final ? `${((T.cur / T.final) * 100).toFixed(0)}% επιτεύχθηκε` : ""}</span>, "pace-kpi-final")}
        {kpi("Νύχτες · ADR", `${N.cur} · ${fmtEuro0(N.cur ? T.cur / N.cur : 0)}`, <span className="text-[11px] text-[#6F6A60]">{cmp}: {N.stly} · {fmtEuro0(N.stly ? T.stly / N.stly : 0)}</span>, "pace-kpi-nights")}
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid="pace-chart">
        <h2 className="text-base md:text-lg font-medium text-[#45423C] mb-3">Έσοδα δωματίων ανά μήνα</h2>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chart} margin={{ left: 0, right: 6 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E3DED4" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6F6A60" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#6F6A60" }} axisLine={false} tickLine={false} width={56} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v) => fmtEuro0(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E3DED4", fontSize: 12 }} cursor={{ fill: "#F7F5F1" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
            <Bar dataKey="final" name={`${cmp} τελικό`} fill="#E3DED4" radius={[4, 4, 0, 0]} />
            <Bar dataKey="stly" name={`${cmp} ίδια ημερομηνία`} fill="#B9AE9A" radius={[4, 4, 0, 0]} />
            <Bar dataKey="cur" name={`${year} on the books`} fill="#C86235" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-x-auto">
        <table className="w-full text-sm" data-testid="pace-table">
          <thead>
            <tr className="bg-[#45423C] text-white text-[10px] uppercase tracking-wider text-right">
              <th className="px-3 py-2.5 text-left">Μήνας</th>
              <th className="px-2 py-2.5">Κρατ. {year}</th><th className="px-2 py-2.5">Νύχτες {year}</th><th className="px-2 py-2.5">Νύχτες {cmp} ίδια ημ.</th>
              <th className="px-2 py-2.5">Έσοδα {year}</th><th className="px-2 py-2.5">Έσοδα {cmp} ίδια ημ.</th><th className="px-2 py-2.5">vs {cmp}</th>
              <th className="px-2 py-2.5">Τελικό {cmp}</th><th className="px-2 py-2.5">ADR {year}</th><th className="px-3 py-2.5">ADR {cmp}</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m) => (
              <tr key={m.m} className="border-t border-[#EFEBE3] text-right tabular" data-testid={`pace-row-${m.m}`}>
                <td className="px-3 py-2 text-left font-medium text-[#45423C]">{MONTHS_EL_SHORT[m.m]}</td>
                <td className="px-2 py-2">{m.cur.res}</td><td className="px-2 py-2">{m.cur.nights}</td><td className="px-2 py-2 text-[#6F6A60]">{m.stly.nights}</td>
                <td className="px-2 py-2 font-semibold">{fmtEuro0(m.cur.rev)}</td><td className="px-2 py-2 text-[#6F6A60]">{fmtEuro0(m.stly.rev)}</td>
                <td className="px-2 py-2"><Delta cur={m.cur.rev} ly={m.stly.rev} /></td>
                <td className="px-2 py-2 text-[#B9AE9A]">{fmtEuro0(m.final.rev)}</td><td className="px-2 py-2">{fmtEuro0(m.cur.adr)}</td><td className="px-3 py-2 text-[#6F6A60]">{fmtEuro0(m.stly.adr)}</td>
              </tr>
            ))}
            {months.length > 0 && (
              <tr className="bg-[#F1EDE6] font-semibold text-right tabular" data-testid="pace-total">
                <td className="px-3 py-2.5 text-left">Σύνολο έτους</td>
                <td className="px-2 py-2.5">{sum(months, "cur", "res")}</td><td className="px-2 py-2.5">{N.cur}</td><td className="px-2 py-2.5">{N.stly}</td>
                <td className="px-2 py-2.5">{fmtEuro0(T.cur)}</td><td className="px-2 py-2.5">{fmtEuro0(T.stly)}</td><td className="px-2 py-2.5"><Delta cur={T.cur} ly={T.stly} /></td>
                <td className="px-2 py-2.5">{fmtEuro0(T.final)}</td><td className="px-2 py-2.5">{fmtEuro0(N.cur ? T.cur / N.cur : 0)}</td><td className="px-3 py-2.5">{fmtEuro0(N.stly ? T.stly / N.stly : 0)}</td>
              </tr>
            )}
            {isLoading && <tr><td colSpan={10} className="px-4 py-8 text-center text-[#6F6A60]">Φόρτωση…</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[#6F6A60]">Οι ακυρωμένες κρατήσεις δεν μετράνε (το Hotelizer δεν δίνει ημερομηνία ακύρωσης). Δεδομένα από τον τελευταίο συγχρονισμό{data?.synced_at ? `: ${fmtDate(data.synced_at.slice(0, 10))}` : ""}.</p>
    </div>
  );
}
